import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { ProductSideImage } from '../../types';
import { ProductEnvironment } from '../context/productContext';

const ButtonContainer = styled.button`
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    gap: 0;
    padding: 6px;
    background: white;
    border-radius: 16px;
    box-shadow:
        0 10px 25px -5px rgba(0, 0, 0, 0.1),
        0 8px 10px -6px rgba(0, 0, 0, 0.1);
    border: 1px solid #d1d5db;
    cursor: pointer;
    z-index: 50;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
        background: #f9fafb;
        border-color: #a78bfa;
        box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 8px 10px -6px rgba(0, 0, 0, 0.1);
    }
`;

const IconWrapper = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;

    ${ButtonContainer}:hover & {
        background: linear-gradient(135deg, #6d28d9 0%, #9333ea 50%, #db2777 100%);
    }

    svg {
        color: white;
        width: 20px;
        height: 20px;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    transition: all 0.3s ease;
    padding-left: 0px;

    ${ButtonContainer}:hover & {
        max-width: 200px;
        opacity: 1;
        padding-left: 10px;
    }
`;

const Thumbnail = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const TextContent = styled.div`
    text-align: left;
    padding-right: 12px;
    white-space: nowrap;
`;

const Label = styled.p`
    font-size: 10px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0;
`;

const Title = styled.p`
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    margin: 0;
`;

interface BackToProductButtonProps {
    sideImages: ProductSideImage[] | undefined;
    onClose: () => void;
    environment?: ProductEnvironment;
}

function getEnvironmentLabel(environment?: ProductEnvironment): string {
    switch (environment) {
        case 'VIEWER':
            return T._('Back to Viewer', 'Admin');
        case 'COMPOSER':
            return T._('Back to Configurator', 'Admin');
        case 'CUSTOMIZER':
            return T._('Back to Customizer', 'Admin');
        default:
            return T._('Back to Product', 'Admin');
    }
}

/**
 * A button that allows users to navigate back to the product environment (viewer, customizer, or configurator).
 * Only renders when sideImages are available and onClose callback is provided.
 */
export const BackToProductButton: FC<BackToProductButtonProps> = ({ sideImages, onClose, environment }) => {
    return (
        <ButtonContainer onClick={onClose}>
            <IconWrapper>
                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                    <polyline points='15 18 9 12 15 6' />
                </svg>
            </IconWrapper>
            <ContentWrapper>
                {sideImages && sideImages.length > 0 && (
                    <Thumbnail>
                        <img src={sideImages[0].url} alt={sideImages[0].sideName || 'Product'} />
                    </Thumbnail>
                )}
                <TextContent>
                    <Label>{T._('Product', 'Admin')}</Label>
                    <Title>{getEnvironmentLabel(environment)}</Title>
                </TextContent>
            </ContentWrapper>
        </ButtonContainer>
    );
};
